import { useSyncExternalStore } from 'react';

// Chromium's install prompt — not part of the standard DOM typings.
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
}

// Chrome, Edge and Samsung Internet hand over an install prompt once they judge
// the site installable, which takes a tap on the page and ~30 seconds spent on
// it. That can happen on any page — usually an event link, long before the home
// page is mounted — so the prompt is caught at module load (main.tsx imports
// this file for that) and kept out here rather than in component state.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

const setDeferredPrompt = (prompt: BeforeInstallPromptEvent | null) => {
    deferredPrompt = prompt;
    listeners.forEach((listener) => listener());
};

window.addEventListener('beforeinstallprompt', (e) => {
    // Our own button stands in for the browser's install banner
    e.preventDefault();
    setDeferredPrompt(e as BeforeInstallPromptEvent);
});

window.addEventListener('appinstalled', () => setDeferredPrompt(null));

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getSnapshot = () => deferredPrompt;

// iPadOS 13+ asks for the desktop site and reports itself as a Mac; the touch
// screen is what gives it away.
const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Launched from the home screen icon rather than a browser tab
const isInstalled =
    window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * How this browser lets the app be installed, if at all:
 * - `prompt` — Chromium has handed over its install dialog; `install()` opens it.
 * - `ios` — Safari has no install dialog, and nothing a page can call to open
 *   one. Share → Add to Home Screen by hand is the only way, so the caller walks
 *   the user through it.
 * - `null` — already installed, or a browser that offers neither (Firefox,
 *   desktop Safari, in-app browsers on Android).
 */
export const useInstallApp = () => {
    const prompt = useSyncExternalStore(subscribe, getSnapshot);

    let method: 'prompt' | 'ios' | null = null;
    if (!isInstalled) {
        if (prompt) method = 'prompt';
        else if (isIOS) method = 'ios';
    }

    const install = () => {
        if (!prompt) return;
        // A prompt can only be shown once. If it is dismissed, Chrome hands over
        // a fresh one straight away, which brings the button back.
        setDeferredPrompt(null);
        prompt.prompt().catch((error) => console.error('Error showing install prompt:', error));
    };

    return { method, install };
};
